import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	OnChanges,
	SimpleChanges,
	Inject,
	ViewChild
} from '@angular/core';
import { MatDialogModule, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

import {
	faComments,
	faSearch,
	faFilter,
	faSort,
	faCommentDots,
	faCommentCheck
} from '@awesome.me/kit-bde9a98016/icons/sharp/solid';

import { AhsTranslationService } from '@nx-ahs/base-translate';
import { GoogleAnalyticsService } from '@nx-ahs/shared/services';
import { SecureMessagingService, SubjectDescription } from '../../services/secure-messaging.service';

import { ContactAccountModel } from '../../models/contact-account.model';
import { ContactUsModel } from '../..//models/contact-us.model';
import { ConversationCategoryModel } from '../../models/conversation-category.model';
import { ConversationCloseWarningComponent } from '../../components/conversation-close-warning/conversation-close-warning.component';
import { ConversationModel } from '../../models/conversation.model';
import { ConversationResponseTypeModel } from '../../models/conversation-response-type.model';
import { CreateNewMessageComponent } from '../../components/create-new-message/create-new-message.component';
//import { ChatPanelComponent } from '../../components/chat-panel/chat-panel.component';
import { ContactTimeModel } from '../../models/contact-time.model';

@Component({
	selector: 'secure-messaging',
	templateUrl: './secure-messaging.component.html',
	styleUrls: ['./secure-messaging.component.scss'],
	standalone: false
})
export class SecureMessagingComponent implements OnInit {
	public faIcons = {
		faComment: faComments,
		faCommentDots: faCommentDots,
		faCommentCheck: faCommentCheck,
		faSearch: faSearch,
		faFilter: faFilter,
		faSort: faSort
	};

	public contactTimes: ContactTimeModel[] = [];
	public conversations: ConversationModel[] = [];
	public convResponseTypes: ConversationResponseTypeModel[] = [];
	public filteredConversations: ConversationModel[] = [];
	public messageSubjects: ConversationCategoryModel[] = [];
	public selectedConversation: ConversationModel;

	public filterValue: string = '';
	public innerWidth: any;
	public isAutoFocus: boolean = false;
	public isTaskOpen: boolean;
	public messageOpen: boolean = false;
	public newMessageForm: FormGroup; //
	public selectedConversationId: number;
	public sideNavType: 'over' | 'push' | 'side' = 'over';
	public snackBarMessage: MatSnackBarRef<SimpleSnackBar>;
	public subjectDescriptions: SubjectDescription[] = [];

	public loadingData = {
		fetchAccounts: false,
		fetchMessages: false,
		fetchMessageSubjects: false,
		fetchConvResponseTypes: false,
		sendingMessage: false
	};

	// changing account models needs the messages filtered again to match the accounts
	private _accountModels: ContactAccountModel[] = [];
	@Input()
	get accountModels(): ContactAccountModel[] {
		return this._accountModels;
	}
	set accountModels(accountModels: ContactAccountModel[]) {
		this._accountModels = accountModels;
		this.filterMessages();
		this.getRefData();
	}

	@Input() autoFocus?: boolean;
	@Input() healthTrackMode: boolean = false;
	@Input() conversationId: number;
	@Input() messagesOnly: boolean = false;
	@Input() languages: any;
	@Input() userEntityNameId: number = 1;
	@Input() showDisplayName: boolean = false; // Will toggle displaying the associated member name.
	@Input() showPhoneLink: boolean = false; // Will toggle displaying the phone number in the header description as a link.

	@Output() conversationSelected: EventEmitter<number> = new EventEmitter<number>();
	@Output() selectedConversationModel: EventEmitter<ConversationModel> = new EventEmitter<ConversationModel>();
	@Output() accountsAndMessagesLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

	//@ViewChild('chatpanel')
	//chatPanel: ChatPanelComponent;

	constructor(
		private ahsTranslationService: AhsTranslationService,
		private formBuilder: FormBuilder,
		private dialog: MatDialog,
		private gaService: GoogleAnalyticsService,
		private secureMessagingService: SecureMessagingService,
		private snackBar: MatSnackBar,
		@Inject('securityService') public securityService: any
	) {}

	async ngOnInit() {
		//if languages doesn't exist, don't attempt to load translations
		if (this.languages) {
			this.ahsTranslationService.loadTranslations(...this.languages.locales);
		}

		this.buildForm();
		await this.getRefData();
		if (this.accountModels.length == 0) await this.fetchAccounts();
		await this.getMessages();
		this.accountsAndMessagesLoaded.emit(true);

		this.innerWidth = window.innerWidth;
		this.figureSideNavOver();
	}

	private buildForm() {
		this.newMessageForm = this.formBuilder.group({
			message: [null, [Validators.required]]
		});
	}

	public figureSideNavOver() {
		this.innerWidth = window.innerWidth;
		if (this.innerWidth > 900) this.sideNavType = 'side';
		else this.sideNavType = 'over';

		/*
		||  the double NOT(!!) will explicitly force the conversion of any value to the corresponding boolean primitive.
		||    If value not provided for Input autoFocus, default isAutoFocus to false when mode is set to side
		||    otherwise default to true
		*/
		this.isAutoFocus = this.autoFocus == null ? (this.sideNavType === 'side' ? false : true) : !!this.autoFocus;
	}

	private async fetchAccounts(): Promise<void> {
		this.loadingData.fetchAccounts = true;

		const memberList: any[] = await this.secureMessagingService
			.getAccounts(this.securityService.getCurrentUser().userId, this.healthTrackMode)
			.toPromise();
		memberList.forEach((p) => {
			this.mapPersonToModel(p);
		});

		this.loadingData.fetchAccounts = false;
	}

	private findUniqueAccountIds(): number[] {
		const allUniqueAccountIds = [];

		this.accountModels.forEach((p) => {
			if (allUniqueAccountIds.indexOf(p.accountId) === -1) allUniqueAccountIds.push(p.accountId);
		});

		return allUniqueAccountIds;
	}

	public filterMessages() {
		// if the account id is utilized in the conversation, this checks to make sure that only conversations that match the current account id the user is viewing
		// if account id is not being saved as a proprty, show the conversation
		const allUniqueAccountIds = this.findUniqueAccountIds();
		const acctFilter = this.conversations.filter((t) => {
			const accountProp = t.conversationProperties[0].find((p) => p.propertyName == 'ACCOUNTID');
			if (!this.healthTrackMode && accountProp) {
				return allUniqueAccountIds.indexOf(+accountProp.propertyValue) !== -1;
			} else return true;
		});

		if (this.filterValue) {
			this.filteredConversations = acctFilter.filter((t) => {
				if (
					(t.translatedCategory &&
						t.translatedCategory.toLocaleLowerCase().indexOf(this.filterValue.toLocaleLowerCase()) != -1) ||
					t.messages.some((tm) => tm.text.toLocaleLowerCase().includes(this.filterValue.toLocaleLowerCase()))
				) {
					return true;
				} else return false;
			});
		} else this.filteredConversations = acctFilter;
	}

	public getConversationResponseTypes(responseTypes: ConversationResponseTypeModel[]): void {
		this.loadingData.fetchConvResponseTypes = true;

		this.convResponseTypes = responseTypes;

		this.loadingData.fetchConvResponseTypes = false;
	}

	private async getRefData(): Promise<void> {
		const refData = await this.secureMessagingService.getCommRefData().toPromise();
		const categoryList: ConversationCategoryModel[] = refData.conversationCategories.filter((x) => x.isActive);
		const responseTypeList: ConversationResponseTypeModel[] = refData.conversationResponseTypes.filter(
			(x) => x.isActive
		);
		this.getMessageSubjects(categoryList);
		this.getConversationResponseTypes(responseTypeList);
		this.contactTimes = refData.contactTimes
			.filter((x) => x.isActive)
			.sort((a: ContactTimeModel, b: ContactTimeModel) => (a.sortOrder > b.sortOrder ? 1 : -1));
	}

	public async getMessages(): Promise<void> {
		this.loadingData.fetchMessages = true;
		let conversations: ConversationModel[] = [];
		if (!this.conversationId) {
			conversations = await this.secureMessagingService.getMessages().toPromise();
		} else {
			conversations = await this.secureMessagingService.getConversation(this.conversationId).toPromise();
		}
		this.processReturnedConversations(conversations);
		if (this.messagesOnly && this.conversations && this.conversations.length > 0) {
			this.goToMessage(this.conversations[0].conversationId);
		}
		this.loadingData.fetchMessages = false;
	}

	public getMessageSubjects(categories: ConversationCategoryModel[]): void {
		this.loadingData.fetchMessageSubjects = true;

		let filteredCategories: ConversationCategoryModel[];

		if (this.accountModels[0]?.registeredEntityNameId) {
			const registered = this.accountModels[0].registeredEntityNameId['entityNameIds'];

			/*let filteredCategories: ConversationCategoryModel[] = categories.filter(f => f.registeredEntityNameIds != registered);*/
			filteredCategories = categories.filter((f) => {
				if (f.registeredEntityNameIds === null) return true; //// if this is not implemented in another contract just show option

				const entityNameIds = JSON.parse(f.registeredEntityNameIds)['entityNameIds'];

				let r = false;
				Object.entries(entityNameIds).forEach(([key, value], index) => {
					///each KVP in registeredEntityNameIds on Category
					Object.entries(value).forEach(([key, value], index) => {
						///each entityNameId in registeredEntityNameIds on Category
						const categoryEntityNameId = value;
						Object.entries(registered).forEach(([key, value], index) => {
							///each KVP from portalaccount registered entityNames
							Object.entries(value).forEach(([key, value], index) => {
								///each entityNameId in portalaccount registered entityNames
								if (categoryEntityNameId === value && r === false) {
									r = true;
									return;
								}
							});
						});
					});
				});

				return r; ///shows message subject	in dropdown if true
			});
		} else {
			filteredCategories = categories;
		}
		//  If "General Info" is an option, place it at the end of array after the sort process.
		// TODO: do the magic sorting anywhere besides here
		let generalInfoEntry: ConversationCategoryModel[] = [];
		const idx = filteredCategories.findIndex((c) => c.codeName.toLowerCase() === 'generalinformation');
		if (idx != -1) {
			generalInfoEntry = filteredCategories.splice(idx, 1);
		}
		this.messageSubjects = filteredCategories.sort((a, b) => a.description.localeCompare(b.description));

		if (generalInfoEntry.length > 0) {
			this.messageSubjects.push(generalInfoEntry[0]);
		}

		this.loadingData.fetchMessageSubjects = false;
	}

	public goToMessage($event) {
		this.figureSideNavOver();
		this.newMessageForm.reset(); //this.chatPanel.newMessageForm.reset();

		this.selectedConversationId = $event;
		this.selectedConversation = this.conversations.find((t) => t.conversationId == this.selectedConversationId);
		if (!this.messageOpen) {
			this.messageOpen = true;
		}
		this.conversationSelected.emit(this.selectedConversationId);
		this.selectedConversationModel.emit(this.selectedConversation);

		this.secureMessagingService
			.setMessageAsRead(
				this.selectedConversation.messages,
				this.selectedConversationId,
				this.healthTrackMode,
				this.userEntityNameId
			)
			.then((result) => {
				this.updateHasUnreadMessages();
			})
			.catch((data) => {
				console.error(data.error.message);
				this.snackBarMessage = this.snackBar.open(data.error.message, 'DISMISS', { duration: 10000 });
			});
	}

	private isFormInvalid() {
		if (this.newMessageForm.invalid) {
			this.newMessageForm.controls['message'].markAsTouched();
			return true;
		} else return false;
	}

	private makeObjectsOfMessageProperties() {
		this.conversations.forEach((t) => {
			t.conversationProperties = [];
			try {
				t.conversationProperties.push(JSON.parse(t.messageProperty));
			} catch (error) {
				console.error(error);
			}
		});
	}

	private mapPersonToModel(p: any): void {
		const model: ContactAccountModel = new ContactAccountModel();
		model.fullname = p.displayName;
		model.personId = p.personId;
		model.accountId = this.securityService.getCurrentUser().userId; // TODO: this is not really account id. Ultimately sets [WithEntityId], and "accountId" is only sometimes correct

		this.accountModels.push(model);
	}

	public markConversationClosed() {
		const convId: number = this.selectedConversation.conversationId;
		this.secureMessagingService
			.changeConversationStatus(convId, 'Closed')
			.toPromise()
			.then(async (result) => {
				await this.getMessages();
				this.selectedConversation = this.conversations.find((t) => t.conversationId == convId);
				//this.chatPanel.newMessageForm.reset();
			})
			.catch((data) => {
				this.snackBarMessage = this.snackBar.open(data.error.message, 'DISMISS');
			});
	}

	public markConversationOpen(): void {
		this.secureMessagingService
			.changeConversationStatus(this.selectedConversation.conversationId, 'Open')
			.toPromise()
			.then(async (result) => {
				const model = new ContactUsModel();
				model.conversationId = this.selectedConversation.conversationId;
				model.messageEntityNameId = this.selectedConversation.withEntityNameId;
				model.messageEntityId = this.selectedConversation.withEntityId;
				model.message =
					this.ahsTranslationService.translate.instant('STATUS_CHANGED_TO') +
					this.ahsTranslationService.translate.instant('OPEN');
				await this.secureMessagingService.saveMessage(model).toPromise();
				await this.getMessages();
				this.selectedConversation = this.conversations.find((t) => t.conversationId == model.conversationId);
				//this.chatPanel.newMessageForm.reset();
			})
			.catch((data) => {
				this.snackBarMessage = this.snackBar.open(data.error.message, 'DISMISS');
			});
	}

	private processReturnedConversations(conversations: ConversationModel[]) {
		this.conversations.length = 0;
		this.conversations = conversations;
		this.updateHasUnreadMessages();
		this.makeObjectsOfMessageProperties();
		this.filteredConversations.length = 0;
		this.filteredConversations = this.conversations;
		this.filterMessages();
	}

	public sendMessage() {
		if (!this.isFormInvalid()) {
			this.loadingData.sendingMessage = true;
			const model = new ContactUsModel();
			model.conversationId = this.selectedConversation.conversationId;
			model.conversationTypeId = this.selectedConversation.conversationCategory.conversationTypeId;
			if (!this.healthTrackMode) {
				model.messageEntityNameId = this.selectedConversation.withEntityNameId;
				model.messageEntityId = this.selectedConversation.withEntityId;
			} else {
				model.messageEntityNameId = 1; //TODO MAGIC NUMBER
				model.messageEntityId = this.securityService.getCurrentUser().userId;
			}
			model.message = this.newMessageForm.get('message').value;
			this.secureMessagingService
				.saveMessage(model)
				.toPromise()
				.then(async () => {
					await this.getMessages();
					this.selectedConversation = this.conversations.find(
						(t) => t.conversationId == model.conversationId
					);
					this.newMessageForm.reset();
				})
				.catch((data) => {
					this.snackBarMessage = this.snackBar.open(data.error.message, 'DISMISS');
				})
				.finally(() => {
					this.loadingData.sendingMessage = false;
				});
		}
	}

	public showCloseMessageWarningDialog() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.data = {
			accounts: this.accountModels,
			subjects: this.messageSubjects
		};

		const dialogRef = this.dialog.open(ConversationCloseWarningComponent, {
			data: dialogConfig,
			panelClass: '',
			width: '300px',
			maxHeight: '80vh'
		});

		dialogRef.afterClosed().subscribe((result) => {
			console.log('dialog result ' + result);
			if (result) {
				this.markConversationClosed();
			}
		});
	}

	public showNewMessageDialog() {
		this.gaService.eventEmitter('secure_message', 'open', 'New message modal');

		const dialogConfig = new MatDialogConfig();
		dialogConfig.data = {
			accounts: this.accountModels,
			subjects: this.messageSubjects,
			convResponseTypes: this.convResponseTypes,
			contactTimes: this.contactTimes
		};

		const dialogRef = this.dialog.open(CreateNewMessageComponent, {
			data: dialogConfig,
			maxHeight: '80vh',
			width: '450px'
		});

		dialogRef.afterClosed().subscribe(async (result) => {
			if (result) {
				this.gaService.eventEmitter('secure_message', 'submit', 'New message modal');
				await this.getMessages();
			} else {
				this.gaService.eventEmitter('secure_message', 'cancel', 'New message modal');
			}
		});
	}

	public toggleMessageView() {
		if (this.messageOpen) {
			this.selectedConversationId = undefined;
		}
		this.messageOpen = !this.messageOpen;
	}

	private updateHasUnreadMessages() {
		this.conversations.forEach((c) => {
			c.hasUnreadMessages = c.messages.some((m) => !m.readDate && m.ahsUser);
		});
	}
}
